/**
 * Import Mapping Configuration
 *
 * Central config for mapping Excel columns to database property fields.
 * Used by both ProjectAdminUploadTab and AgentImportTab.
 */

// --------------- DB field definitions ---------------

export const LISTING_STATUSES = [
    "ACTIVE", "PENDING", "SOLD", "RENTED",
    "OFF MARKET", "EXPIRED", "CANCELLED", "WITHDRAWN", "INCOMPLETE",
];
export const MLS_LISTING_STATUSES = [
    "Active", "Coming Soon", "Active Under Contract",
    "Sold", "Rented", "Closed", "Temp Off-Market", "Pending", "Cancelled", "Withdrawn", "Expired",
];

export const HOME_TYPES = [
    { value: "SFH", label: "Single Family Home" },
    { value: "Condo", label: "Condominium" },
    { value: "TH", label: "Townhouse" },
    { value: "Villa", label: "Villa" },
    { value: "CoachHome", label: "Coach Home" },
];

export const LISTING_TYPES = [
    { value: "Residential Sale", label: "Residential Sale" },
    { value: "Residential Rental", label: "Residential Rental" },
];

export const COMPASS_OPTIONS = ["None", "E", "W", "S", "N", "NE", "NW", "SE", "SW"];

export const PRIVATE_POOL_OPTIONS = ["Yes", "No", "Unknown"];
export const COMMUNITY_POOL_OPTIONS = ["Yes", "No", "Unknown"];
export const WATER_VIEW_OPTIONS = ["YES", "NO", "UKN"];
export const FURNITURE_OPTIONS = ["None", "Part", "Fully", "UKN"];
export const GARAGE_OPTIONS = ["0", "1-car", "2-cars", "3-cars or more", "Unknown"];
export const RENTAL_TYPE_OPTIONS = ["Annual (1 Year or More)", "Monthly (1 to 11 Months)", "Short Term (less than 1 month)", "UNKNOWN"];
export const YES_NO_OPTIONS = ["Yes", "No"];
export const RCR_OPTIONS = ["YES", "NO", "UKN"];
export const PETS_OPTIONS = ["Yes", "No", "UKN"];
export const HOMESTEAD_OPTIONS = ["FULL", "PARTIL", "NONE"];
export const REQUIRED_YES_NO_OPTIONS = ["YES", "NO"];

// Ordered list of importable database fields
export const DB_FIELDS = [
    { key: "listing_type", label: "Listing type", inputType: "select", options: LISTING_TYPES.map(t => t.value) },
    { key: "mls_number", label: "MLS #", inputType: "text" },
    { key: "price_or_rent", label: "Asking price / rent", inputType: "text" },
    { key: "listing_entry_date", label: "Listing entry date", inputType: "text" },
    { key: "date_became_active", label: "MLS list date", inputType: "text" },
    { key: "mls_listing_status", label: "MLS listing status", inputType: "select", options: MLS_LISTING_STATUSES },
    { key: "home_type", label: "Property type", inputType: "select", options: HOME_TYPES.map(t => t.value) },
    { key: "country", label: "Country", inputType: "text" },
    { key: "community_name", label: "Community name", inputType: "select", options: [] },
    { key: "community_short_name", label: "Community short name", inputType: "select", options: [] },
    { key: "subdivision_name", label: "Subdivision name", inputType: "select", options: [] },
    { key: "subdivision_short", label: "Subdivision short", inputType: "select", options: [] },
    { key: "county_property_id", label: "County property ID", inputType: "text" },
    { key: "street_number", label: "Building no", inputType: "text" },
    { key: "compass_direction", label: "Compass direction", inputType: "select", options: COMPASS_OPTIONS },
    { key: "street_name", label: "Street name", inputType: "text" },
    { key: "post_street_direction", label: "Post street direction", inputType: "select", options: COMPASS_OPTIONS },
    { key: "unit_number", label: "Unit #", inputType: "text" },
    { key: "city", label: "City", inputType: "text" },
    { key: "state", label: "State", inputType: "text" },
    { key: "county_name", label: "County", inputType: "text" },
    { key: "zip_code", label: "Zip code", inputType: "text" },
    { key: "short_address", label: "Short address", inputType: "text" },
    { key: "very_short_address", label: "Very short address", inputType: "text" },
    { key: "bedrooms", label: "Bedrooms", inputType: "text" },
    { key: "bathrooms", label: "Total bathrooms", inputType: "text" },
    { key: "half_bathrooms", label: "Half bathrooms", inputType: "text" },
    { key: "sf_under_ac", label: "SF under AC", inputType: "text" },
    { key: "year_built", label: "Year built", inputType: "text" },
    { key: "private_pool", label: "Private pool", inputType: "select", options: PRIVATE_POOL_OPTIONS },
    { key: "community_pool", label: "Community pool", inputType: "select", options: COMMUNITY_POOL_OPTIONS },
    { key: "garage", label: "Garage", inputType: "select", options: GARAGE_OPTIONS },
    { key: "water_view", label: "Water view", inputType: "select", options: WATER_VIEW_OPTIONS },
    { key: "rcr_buyer", label: "RCR buyer", inputType: "select", options: RCR_OPTIONS },
    { key: "rcr_seller", label: "RCR seller", inputType: "select", options: RCR_OPTIONS },
    { key: "rcr_tenant", label: "RCR tenant", inputType: "select", options: RCR_OPTIONS },
    { key: "rcr_landlord", label: "RCR landlord", inputType: "select", options: RCR_OPTIONS },
    { key: "mls_link", label: "MLS link", inputType: "text" },
    { key: "county_property_link", label: "County property link", inputType: "text" },
    { key: "cw_private_notes", label: "CW private notes", inputType: "text" },
    { key: "cw_public_notes", label: "CW public notes", inputType: "text" },
    { key: "deviation_from_original_data", label: "Deviation from original data", inputType: "select", options: REQUIRED_YES_NO_OPTIONS },
    { key: "anomaly", label: "Anomaly", inputType: "select", options: REQUIRED_YES_NO_OPTIONS },
    { key: "furniture", label: "Furniture", inputType: "select", options: FURNITURE_OPTIONS },
    { key: "pets", label: "Pets", inputType: "select", options: PETS_OPTIONS },
    { key: "homestead", label: "Homestead", inputType: "select", options: HOMESTEAD_OPTIONS },
    { key: "available_date", label: "Available date", inputType: "text" },
    { key: "original_asking_price", label: "Original asking price", inputType: "text" },
    { key: "original_asking_rent", label: "Original asking rent", inputType: "text" },
    { key: "previous_sold_price", label: "Previous sold price", inputType: "text" },
    { key: "previous_sold_date", label: "Previous sold date", inputType: "text" },
    { key: "dom", label: "DOM", inputType: "text" },
    { key: "real_estate_tax", label: "Real estate tax", inputType: "text" },
    { key: "county_assessed_value", label: "County assessed value", inputType: "text" },
    { key: "date_became_sold", label: "MLS sold date", inputType: "text" },
    { key: "date_became_pending", label: "MLS pending date", inputType: "text" },
    { key: "date_became_off_market", label: "MLS off market date", inputType: "text" },
    { key: "date_became_cancelled", label: "MLS cancelled date", inputType: "text" },
    { key: "date_became_withdrawn", label: "MLS withdrawn date", inputType: "text" },
    { key: "date_became_expired", label: "MLS expiration date", inputType: "text" },
    { key: "mls_sold_price", label: "MLS sold price", inputType: "text" },
    { key: "rental_type", label: "Rental type", inputType: "select", options: RENTAL_TYPE_OPTIONS },
    { key: "rented_price", label: "Rented price", inputType: "text" },
];

// --------------- Default column map ---------------
// Map of common Excel header strings (lowercased & trimmed) → DB field key.
// Includes both the "standard" names used by the existing backend `columnMap`
// AND the shorthand names from the user's real-world Excel.

const RAW_COLUMN_MAP = {
    // === Standard backend names ===
    "ML#": "mls_number",
    "Listing Type": "listing_type",
    "Asking Price": "price_or_rent",
    "Listing Entry Date": "listing_entry_date",
    "Listing Date": "listing_entry_date",
    "MLS Listing Status": "mls_listing_status",
    "Property Type": "home_type",
    "County Property Id": "county_property_id",
    "Building No": "street_number",
    "Compass": "compass_direction",
    "Street Name": "street_name",
    "Post Street Direction": "post_street_direction",
    "Unit #": "unit_number",
    "City Name": "city",
    "State": "state",
    "Zip Code": "zip_code",
    "County": "county_name",
    "Short Address": "short_address",
    "Bedrooms": "bedrooms",
    // Holds every bathroom, halves included — see cw-admin importMappingConfig.js.
    "Total Bathrooms": "bathrooms",
    "Full Bathrooms": "bathrooms",
    "Ful Bathrooms": "bathrooms",
    "Half Bathrooms": "half_bathrooms",
    "SF Under AC": "sf_under_ac",
    "Year Built": "year_built",
    "Private Pool": "private_pool",
    "Community Pool": "community_pool",
    "Garage": "garage",
    "Water View": "water_view",
    "Furniture": "furniture",
    "MLS Link": "mls_link",
    "County Property Link": "county_property_link",
    "CW Private Notes": "cw_private_notes",
    "CW Public Notes": "cw_public_notes",
    "RCR Notes": "cw_private_notes",
    "RCR Seller": "rcr_seller",
    "RCR Landlord": "rcr_landlord",
    "RCR Buyer": "rcr_buyer",
    "RCR Tenant": "rcr_tenant",
    "Asking Rent": "price_or_rent",
    "Available Date": "available_date",
    "Sold Date": "date_became_sold",
    "Sold Price": "mls_sold_price",
    "MLS Sold Price": "mls_sold_price",
    "Rental Type": "rental_type",
    "Rented Price": "rented_price",
    "Original Asking Price": "original_asking_price",
    "Original Asking Rent": "original_asking_rent",
    "Previous Sold Price": "previous_sold_price",
    "Previous Sold Date": "previous_sold_date",
    "DOM": "dom",
    "Days On Market": "dom",
    "Real Estate Tax": "real_estate_tax",
    "County Assessed Value": "county_assessed_value",
    "Community Name": "community_name",
    "Homestead": "homestead",
    "Deviation From Original Data": "deviation_from_original_data",
    "Anomaly": "anomaly",

    // === Shorthand names from user's Excel (two-row header merged) ===
    "LISTING TYPE": "listing_type",
    "TYPE": "listing_type",
    "MLS #": "mls_number",
    "MLS#": "mls_number",
    "ASKING PRICE": "price_or_rent",
    "CURRENT ASKING PRICE": "price_or_rent",
    "ASKING RENT": "price_or_rent",
    "CURRENT ASKING RENT": "price_or_rent",
    "PRICE": "price_or_rent",
    "LISTING ENTRY DATE": "listing_entry_date",
    "ENTRY DATE": "listing_entry_date",
    "LISTING ACTIVE DATE": "date_became_active",
    "ACTIVE DATE": "date_became_active",
    "MLS LISTING STATUS": "mls_listing_status",
    "PROPERTY TYPE": "home_type",
    "COUNTRY": "country",
    "COMMUNITY NAME": "community_name",
    "COMMUNITY SHORT NAME": "community_short_name",
    "SUBDIVISION NAME": "subdivision_name",
    "SUBDIVISION SHORT NAME": "subdivision_short",
    "COUNTY PROPERTY ID": "county_property_id",
    "PROPERTY ID": "county_property_id",
    "BUILDING NO": "street_number",
    "NO": "street_number",
    "COMPASS": "compass_direction",
    "STREET NAME": "street_name",
    "POST STREET DIRECTION": "post_street_direction",
    "POST DIRECTION": "post_street_direction",
    "DIRECTION": "post_street_direction",
    "UNIT #": "unit_number",
    "UNIT": "unit_number",
    "CITY NAME": "city",
    "CITY": "city",
    "STATE": "state",
    "COUNTY": "county_name",
    "ZIP CODE": "zip_code",
    "CODE": "zip_code",
    "ZIP": "zip_code",
    "SHORT ADDRESS": "short_address",
    "ADDRESS": "short_address",
    "BEDROOMS": "bedrooms",
    "TOTAL BATHROOMS": "bathrooms",
    "FULL BATHROOMS": "bathrooms",
    "HALF BATHROOMS": "half_bathrooms",
    "SF UNDER AC": "sf_under_ac",
    "SF": "sf_under_ac",
    "UNDER AC": "sf_under_ac",
    "YEAR BUILT": "year_built",
    "BUILT": "year_built",
    "PRIVATE POOL": "private_pool",
    "COMMUNITY POOL": "community_pool",
    "GARAGE": "garage",
    "WATER VIEW": "water_view",
    "VIEW": "water_view",
    "RCR BUYER": "rcr_buyer",
    "BUYER": "rcr_buyer",
    "RCR SELLER": "rcr_seller",
    "SELLER": "rcr_seller",
    "RCR TENANT": "rcr_tenant",
    "TENANT": "rcr_tenant",
    "RCR LANDLORD": "rcr_landlord",
    "LANDLORD": "rcr_landlord",
    "MLS LINK": "mls_link",
    "COUNTY PROPERTY LINK": "county_property_link",
    "COUNTY PROPERY LINK": "county_property_link", // handle typo
    "RCR NOTES": "cw_private_notes",
    "CW PRIVATE NOTES": "cw_private_notes",
    "CW PUBLIC NOTES": "cw_public_notes",
    "NOTES": "cw_public_notes",
    "PRIVATE NOTES": "cw_private_notes",
    "PUBLIC NOTES": "cw_public_notes",
    "HOMESTEAD": "homestead",
    "HMSTD": "homestead",
    "DEVIATION": "deviation_from_original_data",
    "DEVIATION FROM ORIGINAL DATA": "deviation_from_original_data",
    "ANOMALY": "anomaly",
    "FURNITURE": "furniture",
    "AVAILABLE DATE": "available_date",
    "SOLD DATE": "date_became_sold",
    "SOLD PRICE": "mls_sold_price",
    "MLS SOLD PRICE": "mls_sold_price",
    "RENTAL TYPE": "rental_type",
    "RENTED PRICE": "rented_price",
    "ORIG ASKING PRICE": "original_asking_price",
    "ORIGINAL ASKING PRICE": "original_asking_price",
    "ORIG ASKING RENT": "original_asking_rent",
    "ORIGINAL ASKING RENT": "original_asking_rent",
    "PREV SOLD PRICE": "previous_sold_price",
    "PREVIOUS SOLD PRICE": "previous_sold_price",
    "PREV SOLD DATE": "previous_sold_date",
    "PREVIOUS SOLD DATE": "previous_sold_date",
    "DAYS ON MARKET": "dom",
    "REAL ESTATE TAX": "real_estate_tax",
    "COUNTY ASSESS VALUE": "county_assessed_value",
    "COUNTY ASSESSED VALUE": "county_assessed_value",
    "NAME": "street_name",

    // === Normalized snake_case headers from new Excel export ===
    "listing_type": "listing_type",
    "mls_active_date": "date_became_active",
    "mls_list_date": "date_became_active",
    "cw_entry_date": "listing_entry_date",
    "mls_number": "mls_number",
    "asking_price_or_rent": "price_or_rent",
    "mls_listing_status": "mls_listing_status",
    "property_type": "home_type",
    "county_property_id": "county_property_id",
    "subdivision_name": "subdivision_name",
    "subdivision_short_name": "subdivision_short",
    "building_street_number": "street_number",
    "compass": "compass_direction",
    "street_name": "street_name",
    "unit_number": "unit_number",
    "city": "city",
    "state": "state",
    "county": "county_name",
    "zip_code": "zip_code",
    "country": "country",
    "short_address": "short_address",
    "very_short_address": "very_short_address",
    "bedrooms": "bedrooms",
    "bathrooms": "bathrooms",
    "half_bathrooms": "half_bathrooms",
    "sf_under_ac": "sf_under_ac",
    "year_built": "year_built",
    "private_pool": "private_pool",
    "community_pool": "community_pool",
    "garage": "garage",
    "water_view": "water_view",
    "furniture": "furniture",
    "cw_seller": "rcr_seller",
    "mls_link": "mls_link",
    "county_property_link": "county_property_link",
    "cw_notes": "cw_private_notes",
    "cw_public_notes": "cw_public_notes",
    "cw_buyer": "rcr_buyer",
    "cw_tenant": "rcr_tenant",
    "cw_landlord": "rcr_landlord",
    "available_date": "available_date",
    "mls_sold_date": "date_became_sold",
    "mls_rented_date": "date_became_rented",
    "mls_rented date": "date_became_rented",
    "mls_pending_date": "date_became_pending",
    "mls_cancelled_date": "date_became_cancelled",
    "mls_cancelled date": "date_became_cancelled",
    "mls_expiration_date": "date_became_expired",
    "mls_expired_date": "date_became_expired",
    "mls_off_market_date": "date_became_off_market",
    "mls_withdrawn_date": "date_became_withdrawn",
    "post_street_direction": "post_street_direction",
    "sold_price": "mls_sold_price",    // backward-compat
    "mls_sold_price": "mls_sold_price",
    "rental_type": "rental_type",
    "rented_price": "rented_price",
    "original_asking_price": "original_asking_price",
    "original_asking_rent": "original_asking_rent",
    "previous_sold_price": "previous_sold_price",
    "previous_sold_date": "previous_sold_date",
    "dom": "dom",
    "real_estate_tax": "real_estate_tax",
    "county_assessed_value": "county_assessed_value",
};

// Build a lowercase lookup
const LOWERCASE_MAP = {};
for (const [key, val] of Object.entries(RAW_COLUMN_MAP)) {
    LOWERCASE_MAP[key.toLowerCase().trim()] = val;
}

/**
 * Attempt to match an Excel header string to a DB field key.
 * Returns the DB field key or null if no match found.
 */
export function guessFieldForHeader(header) {
    if (!header) return null;
    const clean = String(header).trim();
    // Exact match (case-insensitive), collapse multiple spaces
    const lower = clean.toLowerCase().replace(/\s+/g, " ");
    if (LOWERCASE_MAP[lower]) return LOWERCASE_MAP[lower];
    // Try without leading/trailing special chars
    const stripped = lower.replace(/[^a-z0-9 ]/g, "").trim();
    if (LOWERCASE_MAP[stripped]) return LOWERCASE_MAP[stripped];
    return null;
}

/**
 * Given an array of Excel header strings, return a mapping object:
 * { [columnIndex]: dbFieldKey | null }
 *
 * Uses column index as key (not header string) to avoid collisions
 * when multiple columns share the same header text (e.g. empty headers).
 * Avoids mapping two Excel columns to the same DB field by skipping duplicates.
 */
export function guessMapping(excelHeaders) {
    const mapping = {};
    const usedFields = new Set();
    for (let i = 0; i < excelHeaders.length; i++) {
        const header = excelHeaders[i];
        const field = guessFieldForHeader(header);
        if (field && !usedFields.has(field)) {
            mapping[i] = field;
            usedFields.add(field);
        } else {
            mapping[i] = null; // skip
        }
    }
    return mapping;
}

/**
 * Detect if the parsed Excel has a two-row header structure.
 * Returns the actual header row and data rows.
 *
 * The user's Excel has:
 *   Row 1: Category groups ("LISTING", "PROPERTY", "COMMUNITY", ...)
 *   Row 2: Actual field names ("TYPE", "MLS #", "PRICE", ...)
 *   Row 3+: Data
 *
 * When parsed with XLSX sheet_to_json using header:1, we get an array of arrays.
 * This function handles both single-row and two-row header scenarios.
 */
export function detectHeaders(rawSheetData) {
    if (!rawSheetData || rawSheetData.length < 2) {
        return { headers: [], dataRows: [] };
    }

    const row1 = rawSheetData[0]; // potential category row or header row
    const row2 = rawSheetData[1]; // potential actual header row

    // Heuristic: if row2 has significantly more non-empty cells than row1,
    // or if row1 has many empty cells, treat it as a two-row header.
    const row1NonEmpty = Object.values(row1).filter(v => v !== "" && v !== null && v !== undefined).length;
    const row2NonEmpty = Object.values(row2).filter(v => v !== "" && v !== null && v !== undefined).length;
    const totalCols = Math.max(Object.keys(row1).length, Object.keys(row2).length);

    // Detect two-row header: check if row2 values look like sub-headers
    // (e.g. row1 = category groups, row2 = actual field names)
    // Heuristic: row1 has many duplicate or empty cells AND row2 also has non-empty cells
    const row1Values = Object.values(row1).map(v => String(v || "").trim());
    const row1Unique = new Set(row1Values.filter(Boolean));
    const row1HasDuplicates = row1Unique.size < row1NonEmpty * 0.7;
    const row1HasEmpties = row1NonEmpty < totalCols * 0.85;
    const isTwoRowHeader = totalCols > 3 && row2NonEmpty > row1NonEmpty * 0.5 &&
        (row1HasDuplicates || row1HasEmpties) &&
        row1NonEmpty < totalCols;

    if (isTwoRowHeader) {
        // Merge row1 (category) + row2 (field name) into combined headers
        const headers = [];
        const keys = Object.keys(row2);
        for (let i = 0; i < keys.length; i++) {
            const cat = String(row1[i] || row1[keys[i]] || "").trim();
            const field = String(row2[keys[i]] || "").trim();
            if (cat && field) {
                // Normalize whitespace to avoid double-spaces from trailing spaces in category cells
                headers.push(`${cat} ${field}`.replace(/\s+/g, " "));
            } else {
                headers.push(field || cat || "");
            }
        }
        const dataRows = rawSheetData.slice(2);
        return { headers, dataRows, headerRowIndex: 1 };
    }

    // Single-row header: row1 is the header
    const headers = [];
    const keys = Object.keys(row1);
    for (const k of keys) {
        headers.push(String(row1[k] || "").trim());
    }
    const dataRows = rawSheetData.slice(1);
    return { headers, dataRows, headerRowIndex: 0 };
}
